import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ArticleSchema, BreadcrumbSchema, FAQSchema } from '@/components/SEOSchema';
import { SITE_URL } from '@/lib/seo';

const tocItems = [
  { id: 'intro', label: 'Introdução' },
  { id: 'what-is', label: 'O que é CSSBuy Spreadsheet?' },
  { id: 'how-to-use', label: 'Como Usar o CSSBuy Spreadsheet' },
  { id: 'categories', label: 'Categorias Principais' },
  { id: 'qc', label: 'Quality Check (QC) Explained' },
  { id: 'shipping', label: 'Entenda o Shipping' },
  { id: 'warehouse', label: 'Processo de Warehouse' },
  { id: 'safety', label: 'Dicas de Segurança' },
  { id: 'faq', label: 'Perguntas Frequentes' },
  { id: 'conclusion', label: 'Conclusão' },
];

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const [activeSection, setActiveSection] = useState('intro');

  useEffect(() => {
    const handleScroll = () => {
      const sections = tocItems.map((item) => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 200;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tocItems[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const homePath = i18n.language === 'pt' ? '/' : `/${i18n.language}/`;
  const currentUrl = `${SITE_URL}${i18n.language === 'pt' ? '/cssbuy-spreadsheet-guia/' : i18n.language === 'de' ? '/cssbuy-spreadsheet-anleitung/' : '/cssbuy-spreadsheet-guide/'}`;

  const articleFaqs = [
    { question: 'O CSSBuy Spreadsheet é oficial?', answer: 'Não. O CSSBuy Spreadsheet mantido no cssbuybr.sbs é um recurso independente. Não somos afiliados, endossados ou operados pelo CSSBuy.' },
    { question: 'Como encontro produtos específicos?', answer: 'Use as categorias do menu superior ou navegue pelos cards de categorias na página inicial. Cada categoria contém links organizados.' },
    { question: 'O que fazer se um link não funcionar?', answer: 'Links podem expirar ou mudar. Confirme o link diretamente na plataforma CSSBuy ou tente pesquisar o produto pelo nome.' },
    { question: 'É seguro comprar através do CSSBuy?', answer: 'O CSSBuy é uma plataforma conhecida, mas como qualquer compra online, existem riscos. Sempre use QC, verifique vendedores e confirme informações antes de comprar.' },
    { question: 'Quanto tempo leva o processo completo?', answer: 'O tempo varia: compra pode levar 3-7 dias, envio do vendedor ao warehouse 3-14 dias, QC 1-3 dias, e shipping internacional 7-30 dias dependendo do método.' },
    { question: 'Posso cancelar um pedido após o QC?', answer: 'As políticas de cancelamento variam. Geralmente, antes do envio para o warehouse, cancelamentos são mais fáceis. Após o QC, depende da política do vendedor e do agente.' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <ArticleSchema
        title="CSSBuy Spreadsheet 2026: Guia Completo, Links, Categorias e Recursos para Compradores"
        description="Guia completo independente sobre CSSBuy Spreadsheet. Aprenda a usar, entenda QC, shipping, warehouse e encontre categorias populares."
        url={currentUrl}
        lang={i18n.language}
        datePublished="2026-08-27"
        dateModified="2026-08-27"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: `${SITE_URL}${homePath}` },
          { name: 'CSSBuy Spreadsheet Guia', url: currentUrl },
        ]}
      />
      <FAQSchema faqs={articleFaqs} />

      <Navbar />

      <div className="w-full px-4 md:px-6 lg:px-10 pt-24 pb-16">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6">
            <Link to={homePath} className="hover:text-primary-600 transition-colors">{t('nav.home')}</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-600">CSSBuy Spreadsheet Guia</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Sidebar TOC */}
            <aside className="hidden lg:block lg:col-span-3">
              <div className="sticky top-24 bg-white rounded-xl border border-background-200/60 p-5">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-foreground-400 mb-4">{t('article.toc')}</h4>
                <ul className="space-y-2">
                  {tocItems.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => scrollTo(item.id)}
                        className={`text-left text-sm w-full py-1 px-2 rounded transition-colors ${
                          activeSection === item.id
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-50'
                        }`}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Main Content */}
            <article className="lg:col-span-9">
              {/* Header */}
              <header className="mb-10">
                <span className="text-xs font-semibold uppercase tracking-wider text-primary-600 mb-3 block">Guia Completo · 2026</span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-4 leading-tight">
                  CSSBuy Spreadsheet 2026: Guia Completo, Links, Categorias e Recursos para Compradores
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                  <span className="flex items-center gap-1"><i className="ri-user-line" /> {t('article.author')}</span>
                  <span className="flex items-center gap-1"><i className="ri-calendar-line" /> {t('article.updated')}: 27/08/2026</span>
                  <span className="flex items-center gap-1"><i className="ri-time-line" /> 12 {t('article.readTime')}</span>
                </div>
                <p className="mt-4 text-sm text-foreground-400 italic border-l-2 border-primary-300 pl-4">
                  {t('article.disclaimer')}
                </p>
              </header>

              <div className="prose max-w-none">
                {/* Intro */}
                <section id="intro" className="mb-12">
                  <p className="text-lg text-foreground-600 leading-relaxed mb-4">
                    Se você está buscando informações sobre o <strong>CSSBuy Spreadsheet</strong>, chegou ao lugar certo. Este guia completo foi desenvolvido para ajudar compradores de todos os níveis — desde iniciantes até experientes — a entenderem como navegar, pesquisar e aproveitar ao máximo os recursos disponíveis no universo CSSBuy.
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    O <strong>CSSBuy Spreadsheet</strong> é uma ferramenta organizacional que centraliza links, categorias de produtos, guias de compra e informações essenciais sobre QC (Quality Check), shipping, warehouse e muito mais. Nosso objetivo é oferecer um recurso independente, claro e atualizado que facilite sua jornada de compra.
                  </p>
                  <div className="bg-primary-50/50 border border-primary-200/50 rounded-xl p-5 my-6">
                    <p className="text-sm text-foreground-600">
                      <strong>Aviso importante:</strong> Este website é um recurso informacional independente e não é afiliado, endossado ou operado pelo CSSBuy. Todas as informações são baseadas em pesquisa pública independente e podem estar sujeitas a alterações. Sempre confirme dados diretamente na plataforma CSSBuy antes de realizar compras.
                    </p>
                  </div>
                </section>

                {/* What is */}
                <section id="what-is" className="mb-12">
                  <h2 className="text-2xl md:text-3xl font-semibold mb-4">O que é CSSBuy Spreadsheet?</h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    O <strong>CSSBuy Spreadsheet</strong> é essencialmente uma coleção organizada de links, categorias de produtos e recursos relacionados ao ecossistema de compras através do agente CSSBuy. Diferente de um catálogo tradicional, um spreadsheet oferece flexibilidade na organização, permitindo que compradores filtrem, pesquisem e categorizem itens de forma personalizada.
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    A estrutura típica de um <strong>CSSBuy Spreadsheet</strong> inclui colunas organizadas por categoria, nome do produto, link de compra, preço aproximado, notas de qualidade e informações de vendedor. Essa organização permite que compradores comparem opções rapidamente e tomem decisões mais informadas antes de finalizar suas compras.
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    No <Link to={homePath} className="text-primary-600 hover:underline">cssbuybr.sbs</Link>, mantemos um <strong>CSSBuy Spreadsheet</strong> atualizado com categorias populares como tênis, hoodies, camisetas, jaquetas, calças, bolsas e acessórios. Cada categoria é verificada periodicamente para garantir que os links permaneçam úteis e relevantes para compradores em 2026.
                  </p>
                </section>

                {/* How to use */}
                <section id="how-to-use" className="mb-12">
                  <h2 className="text-2xl md:text-3xl font-semibold mb-4">Como Usar o CSSBuy Spreadsheet: Passo a Passo</h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    Utilizar o <strong>CSSBuy Spreadsheet</strong> de forma eficiente requer entender o fluxo básico de navegação, pesquisa e verificação. Aqui está um passo a passo detalhado para maximizar sua experiência:
                  </p>
                  <div className="space-y-4 mb-6">
                    <div className="bg-white border border-background-200/60 rounded-xl p-5">
                      <h3 className="font-semibold text-lg mb-2">1. Escolha uma Categoria Relevante</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        Comece navegando pelas categorias disponíveis. O CSSBuy Spreadsheet organiza produtos em grupos lógicos como calçados, vestuário superior, acessórios e mais. Escolher a categoria certa desde o início economiza tempo e ajuda a encontrar opções mais relevantes.
                      </p>
                    </div>
                    <div className="bg-white border border-background-200/60 rounded-xl p-5">
                      <h3 className="font-semibold text-lg mb-2">2. Analise Links e Informações do Produto</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        Cada entrada no spreadsheet contém informações cruciais sobre o produto. Verifique descrições, preços aproximados e notas adicionais antes de clicar em qualquer link. Lembre-se que preços podem variar e devem ser confirmados na plataforma.
                      </p>
                    </div>
                    <div className="bg-white border border-background-200/60 rounded-xl p-5">
                      <h3 className="font-semibold text-lg mb-2">3. Verifique QC e Medidas</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        O Quality Check (QC) é uma etapa fundamental. Fotos de QC mostram o produto real no warehouse antes do envio, permitindo verificar qualidade, medidas e condições. Sempre verifique se há fotos QC disponíveis para o produto que você está considerando.
                      </p>
                    </div>
                    <div className="bg-white border border-background-200/60 rounded-xl p-5">
                      <h3 className="font-semibold text-lg mb-2">4. Compare Múltiplas Opções</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        Não se limite à primeira opção que encontrar. Use o spreadsheet para comparar diferentes vendedores, preços e avaliações. A comparação é uma das maiores vantagens de usar um spreadsheet organizado em vez de buscar produtos individualmente.
                      </p>
                    </div>
                    <div className="bg-white border border-background-200/60 rounded-xl p-5">
                      <h3 className="font-semibold text-lg mb-2">5. Confirme Informações Atuais na Plataforma</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        Antes de finalizar qualquer compra, sempre confirme as informações mais recentes diretamente na plataforma CSSBuy. Links, preços, disponibilidade e políticas de envio podem mudar. O spreadsheet é um ponto de partida, mas a confirmação final deve ser feita no site oficial.
                      </p>
                    </div>
                  </div>
                  <div className="bg-accent-50/50 border border-accent-200/50 rounded-xl p-5">
                    <p className="text-sm text-foreground-600">
                      <strong>Dica Pro:</strong> Mantenha uma lista pessoal de produtos favoritos enquanto navega pelo spreadsheet. Isso facilita a comparação final e ajuda a organizar suas compras de forma mais eficiente.
                    </p>
                  </div>
                </section>

                {/* Categories */}
                <section id="categories" className="mb-12">
                  <h2 className="text-2xl md:text-3xl font-semibold mb-4">Categorias Principais do CSSBuy Spreadsheet</h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    O <strong>CSSBuy Spreadsheet</strong> abrange uma ampla variedade de categorias para atender diferentes necessidades e estilos de compra. Aqui estão as principais categorias que você encontrará em nosso recurso:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    {[
                      ['Tênis / Shoes', 'A maior variedade de calçados, desde sneakers casuais até edições especiais. Verifique sempre o tamanho e as medidas disponíveis no QC.'],
                      ['Hoodies / Sweaters', 'Moletom e sweaters em diversos estilos, materiais e cores. Ideal para o inverno ou para looks casuais.'],
                      ['Camisetas / T-Shirts', 'Peças essenciais do guarda-roupa. Variedade de estampas, cortes e tecidos disponíveis.'],
                      ['Jaquetas / Jackets', 'De casacos leves a jaquetas mais pesadas. Preste atenção nas medidas e na tabela de tamanhos.'],
                      ['Calças / Pants & Shorts', 'Inclui jeans, cargo pants, shorts e mais. Verifique o comprimento e a largura nas fotos de QC.'],
                      ['Bolsas / Bags', 'Backpacks, shoulder bags, tote bags e acessórios de viagem. Confirme dimensões no QC.'],
                      ['Acessórios / Accessories', 'Coleiras, correntes, pulseiras e outros complementos de estilo.'],
                      ['Perfumes', 'Fragrâncias e perfumes. Sempre verifique autenticidade e vendedor antes de comprar.'],
                    ].map(([title, desc]) => (
                      <div key={title} className="bg-white border border-background-200/60 rounded-xl p-5">
                        <h4 className="font-semibold mb-2">{title}</h4>
                        <p className="text-sm text-foreground-500">{desc}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-foreground-600 leading-relaxed">
                    Além dessas categorias principais, o <strong>CSSBuy Spreadsheet</strong> também inclui seções para headwear, sets, underwear, jerseys, belts e uma categoria "Other Stuff" para itens diversos. Cada categoria é atualizada regularmente para manter os links úteis e funcionais. Volte para a <Link to={homePath} className="text-primary-600 hover:underline">página inicial</Link> para explorar todas as categorias.
                  </p>
                </section>

                {/* QC */}
                <section id="qc" className="mb-12">
                  <h2 className="text-2xl md:text-3xl font-semibold mb-4">Quality Check (QC): O Que Você Precisa Saber</h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    O <strong>Quality Check (QC)</strong> é uma das etapas mais importantes no processo de compra através de um agente. O QC consiste em fotos detalhadas do produto real tiradas no warehouse do agente antes do envio para o cliente final.
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    Quando um vendedor envia um produto para o warehouse do CSSBuy, o agente fotografa o item de múltiplos ângulos. Essas fotos permitem que você verifique a qualidade, as medidas, as cores e o estado geral do produto antes de aprovar o envio para seu endereço.
                  </p>
                  <h3 className="text-xl font-semibold mb-3">O que verificar nas fotos QC?</h3>
                  <ul className="list-disc list-inside space-y-2 text-foreground-600 mb-4">
                    <li><strong>Medidas:</strong> Compare as medidas reais com a tabela de tamanhos do produto.</li>
                    <li><strong>Qualidade do material:</strong> Observe textura, costuras e acabamento.</li>
                    <li><strong>Cores:</strong> Verifique se a cor corresponde às fotos de venda.</li>
                    <li><strong>Detalhes:</strong> Confira logos, etiquetas e detalhes específicos do produto.</li>
                    <li><strong>Estado:</strong> Certifique-se de que o item não apresenta defeitos visíveis.</li>
                  </ul>
                  <p className="text-foreground-600 leading-relaxed">
                    Se algo não estiver correto nas fotos QC, você pode solicitar uma troca, reembolso ou ajuste antes que o produto seja enviado. Essa é uma das maiores vantagens de usar um agente — a capacidade de verificar antes de receber. Para mais detalhes, consulte nosso <Link to={homePath} className="text-primary-600 hover:underline">CSSBuy QC Guide</Link> na página principal.
                  </p>
                </section>

                {/* Shipping */}
                <section id="shipping" className="mb-12">
                  <h2 className="text-2xl md:text-3xl font-semibold mb-4">Entenda o Shipping: Custo, Métodos e Fatores</h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    O <strong>shipping</strong> é uma das considerações mais importantes ao usar o CSSBuy. Os custos variam significativamente dependendo de vários fatores, e entender essas variáveis ajuda você a planejar melhor suas compras.
                  </p>
                  <h3 className="text-xl font-semibold mb-3">Fatores que Influenciam o Custo de Shipping</h3>
                  <div className="overflow-x-auto mb-6">
                    <table className="w-full text-sm border border-background-200/60 rounded-xl">
                      <thead>
                        <tr className="bg-background-100">
                          <th className="text-left px-4 py-3 font-semibold">Fator</th>
                          <th className="text-left px-4 py-3 font-semibold">Por que importa</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-200/60">
                        {[
                          ['Peso', 'O peso real do pacote é um dos principais determinantes do custo.'],
                          ['Volume', 'O peso volumétrico pode afetar o cálculo, especialmente para itens grandes e leves.'],
                          ['Destino', 'A rota de envio e o país de destino influenciam diretamente o preço.'],
                          ['Método de Envio', 'Diferentes linhas de envio têm preços e velocidades distintas.'],
                          ['Seguro', 'Proteção opcional que pode adicionar um custo extra ao envio.'],
                          ['Embalagem', 'A forma de embalagem pode afetar o tamanho final do pacote.'],
                          ['Consolidação', 'Combinar múltiplos itens em um pacote pode reduzir custos por unidade.'],
                        ].map(([factor, why]) => (
                          <tr key={factor} className="bg-white">
                            <td className="px-4 py-3 font-medium">{factor}</td>
                            <td className="px-4 py-3 text-foreground-500">{why}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-foreground-600 leading-relaxed">
                    <strong>Dica importante:</strong> Sempre confirme o preço final de shipping diretamente na plataforma CSSBuy antes de pagar. Estimativas podem variar, e as condições de envio mudam regularmente. Use a ferramenta de cálculo de shipping disponível na plataforma para obter valores atualizados. Volte para a <Link to={homePath} className="text-primary-600 hover:underline">página inicial</Link> para mais guias sobre CSSBuy Spreadsheet.
                  </p>
                </section>

                {/* Warehouse */}
                <section id="warehouse" className="mb-12">
                  <h2 className="text-2xl md:text-3xl font-semibold mb-4">Processo de Warehouse: Como Funciona</h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    O processo de <strong>warehouse</strong> é o coração operacional do serviço de agente. Entender como funciona ajuda você a ter expectativas realistas e a acompanhar melhor suas compras.
                  </p>
                  <div className="flex flex-col gap-4 mb-6">
                    {[
                      ['1. Compra', 'Você compra um produto através do agente ou fornece um link para compra.'],
                      ['2. Vendedor envia', 'O vendedor envia o produto para o warehouse do CSSBuy na China.'],
                      ['3. Recebimento no Warehouse', 'O CSSBuy recebe o pacote e registra sua chegada no sistema.'],
                      ['4. Quality Check (QC)', 'Fotos detalhadas do produto são tiradas para sua aprovação.'],
                      ['5. Armazenamento', 'O produto permanece em armazenamento até que você aprove o envio.'],
                      ['6. Consolidação', 'Se você comprou múltiplos itens, eles podem ser consolidados em um pacote.'],
                      ['7. Envio', 'Após aprovação, o pacote é enviado para seu endereço final.'],
                    ].map(([step, desc]) => (
                      <div key={step} className="flex items-start gap-4 bg-white border border-background-200/60 rounded-xl p-4">
                        <span className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 font-semibold text-sm flex-shrink-0">{step.split('.')[0]}</span>
                        <div>
                          <h4 className="font-semibold text-sm">{step.split('. ')[1]}</h4>
                          <p className="text-sm text-foreground-500">{desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-foreground-600 leading-relaxed">
                    O tempo de permanência no warehouse pode variar. Alguns agentes oferecem períodos de armazenamento gratuitos, após os quais taxas podem ser aplicadas. Sempre verifique as políticas de armazenamento atualizadas diretamente na plataforma CSSBuy. Acesse nossa <Link to={homePath} className="text-primary-600 hover:underline">página inicial</Link> para explorar mais recursos sobre CSSBuy Spreadsheet.
                  </p>
                </section>

                {/* Safety */}
                <section id="safety" className="mb-12">
                  <h2 className="text-2xl md:text-3xl font-semibold mb-4">Dicas de Segurança para Compradores</h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    A segurança deve ser sempre prioridade ao comprar online, especialmente quando envolve plataformas internacionais e agentes de compra. Aqui estão nossas recomendações essenciais:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    {[
                      ['Verifique URLs', 'Sempre acesse sites diretamente e confirme que está na URL correta.'],
                      ['Confira Vendedores', 'Verifique avaliações e reputação de vendedores antes de comprar.'],
                      ['Meça Antes de Comprar', 'Use as fotos de QC para verificar medidas antes de aprovar.'],
                      ['Revise QC Cuidadosamente', 'Não aprove fotos QC às pressas. Analise cada detalhe.'],
                      ['Confirme Shipping', 'Verifique custos de envio e prazos estimados antes de confirmar.'],
                      ['Leia Políticas', 'Entenda as políticas de reembolso, troca e devolução do agente.'],
                      ['Não Compartilhe Senhas', 'Nunca compartilhe suas credenciais de acesso com ninguém.'],
                      ['Use Métodos Seguros', 'Prefira métodos de pagamento com proteção ao comprador.'],
                    ].map(([title, tip]) => (
                      <div key={title} className="bg-white border border-background-200/60 rounded-xl p-4">
                        <h4 className="font-semibold text-sm mb-1">{title}</h4>
                        <p className="text-sm text-foreground-500">{tip}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-foreground-600 leading-relaxed">
                    Comprar através de agentes envolve riscos inerentes, como qualidade inconsistente, atrasos de envio e possíveis discrepâncias entre fotos de produto e item real. O QC mitiga muitos desses riscos, mas a vigilância do comprador é sempre essencial. Visite nossa <Link to={homePath} className="text-primary-600 hover:underline">página principal</Link> para mais recursos sobre segurança no CSSBuy Spreadsheet.
                  </p>
                </section>

                {/* FAQ */}
                <section id="faq" className="mb-12">
                  <h2 className="text-2xl md:text-3xl font-semibold mb-4">Perguntas Frequentes sobre CSSBuy Spreadsheet</h2>
                  <div className="space-y-4">
                    {articleFaqs.map((f) => (
                      <div key={f.question} className="bg-white border border-background-200/60 rounded-xl p-5">
                        <h4 className="font-semibold mb-2">{f.question}</h4>
                        <p className="text-sm text-foreground-500">{f.answer}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Conclusion */}
                <section id="conclusion" className="mb-12">
                  <h2 className="text-2xl md:text-3xl font-semibold mb-4">Conclusão</h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    O <strong>CSSBuy Spreadsheet</strong> é uma ferramenta poderosa para organizar e facilitar compras internacionais. Quando usado corretamente, ajuda compradores a navegar por milhares de opções, comparar produtos e tomar decisões mais informadas.
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    Lembre-se sempre: este é um recurso independente. Para informações mais atualizadas sobre preços, disponibilidade, políticas de envio e termos de serviço, sempre consulte diretamente o <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:underline">site principal</a> e a plataforma CSSBuy.
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    Continue explorando nossas <Link to={homePath} className="text-primary-600 hover:underline">categorias</Link> e guias para aprofundar seu conhecimento. Nosso objetivo é ajudar você a comprar de forma mais inteligente, segura e informada. Obrigado por usar o CSSBuy Spreadsheet Resource Hub!
                  </p>
                </section>
              </div>

              {/* CTA at end */}
              <div className="bg-primary-50/50 border border-primary-200/50 rounded-xl p-6 mt-10">
                <h3 className="font-semibold text-lg mb-2">Pronto para começar?</h3>
                <p className="text-foreground-600 text-sm mb-4">
                  Explore todas as categorias e recursos do CSSBuy Spreadsheet em nossa página principal.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to={homePath} className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-medium px-5 py-2.5 rounded-lg transition-colors text-sm">
                    <i className="ri-home-line" /> Voltar à Página Inicial
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 bg-white hover:bg-background-50 text-foreground-700 font-medium px-5 py-2.5 rounded-lg border border-background-200 transition-colors text-sm"
                  >
                    Visitar Site Principal <i className="ri-external-link-line" />
                  </a>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}